import { useState, useEffect } from 'react';
import { Table, Badge, Button, Pagination, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen,
  faTrash,
  faEye,
  faCloudArrowUp,
  faBan,
  faCircleQuestion
} from '@fortawesome/free-solid-svg-icons';
import BlogEditor from './BlogEditor';
import BlogPreview from './BlogPreview';
import FaqEditor from './FaqEditor';
import { getBlogs, deleteBlog, publishBlog } from './blogUtils';
import { useAppSelector } from '../../redux/Store';
import { loggedInUserSelector } from '../../redux/slice/LoggedInUserSlice';
import './blog.css';

const BlogList = ({ showAddBlog, setShowAddBlog }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editBlog, setEditBlog] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewBlog, setPreviewBlog] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showFaqEditor, setShowFaqEditor] = useState(false);
  const [selectedBlogForFaq, setSelectedBlogForFaq] = useState(null);
  const { loggedInUser } = useAppSelector(loggedInUserSelector);

  const fetchBlogsData = async (page = 1, category = null) => {
    setLoading(true);
    setError('');

    try {
      const response = await getBlogs(page, 10, category);

      // Handle response data based on API structure
      setBlogs(response.records || response.blogs || response);

      // If pagination info is available in response
      if (response.totalPages) {
        setTotalPages(response.totalPages);
      } else {
        // Fallback - estimate based on total count if available
        const totalCount = response.totalCount || response.length;
        setTotalPages(Math.ceil(totalCount / 10)); // Assuming 10 blogs per page
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to load blogs. Please try again later.');
      setLoading(false);

      // Fallback to empty array on error
      setBlogs([]);
    }
  };

  useEffect(() => {
    fetchBlogsData(currentPage, selectedCategory);
  }, [currentPage, selectedCategory]);

  const handleEdit = (blog) => {
    setEditBlog(blog);
    setShowAddBlog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        // await deleteBlog(slug);
        await deleteBlog(id);
        // Refresh the blog list after successful deletion
        fetchBlogsData(currentPage, selectedCategory);
      } catch (err) {
        console.error('Error deleting blog:', err);
        setError('Failed to delete blog. Please try again later.');
      }
    }
  };

  const handleTogglePublish = async (blog) => {
    try {
      const newStatus = !blog.status;
      await publishBlog(blog.id, newStatus, loggedInUser?.fullName || 'Admin');
      // Refresh the blog list after successful update
      fetchBlogsData(currentPage, selectedCategory);
    } catch (err) {
      console.error('Error publishing blog:', err);
      setError(
        `Failed to ${
          blog.status ? 'unpublish' : 'publish'
        } blog. Please try again later.`
      );
    }
  };

  const handlePreview = (blog) => {
    setPreviewBlog(blog);
    setShowPreview(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // fetchBlogsData will be called via useEffect
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page when filtering
    // fetchBlogsData will be called via useEffect
  };

  const handleSaveBlog = async (blog, isEdit) => {
    try {
      // The actual API call is now handled in BlogEditor
      // Just update our local state based on response
      if (isEdit) {
        setBlogs((prev) => prev.map((b) => (b.slug === blog.slug ? blog : b)));
      } else {
        setBlogs((prev) => [...prev, blog]);
      }

      // Refresh the blog list to ensure we have the latest data
      fetchBlogsData(currentPage, selectedCategory);
    } catch (err) {
      console.error('Error saving blog:', err);
      setError('Failed to save blog. Please try again later.');
    }
  };

  const handleManageFaqs = (blog) => {
    setSelectedBlogForFaq(blog);
    setShowFaqEditor(true);
  };

  const getBreadcrumbItems = () => {
    const items = [
      { label: 'All Blogs', path: '/blog', active: !selectedCategory }
    ];

    if (selectedCategory) {
      items.push({
        label: selectedCategory,
        active: true
      });
    }

    return items;
  };

  const renderPagination = () => {
    const items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="justify-content-center mt-4">
        <Pagination.Prev
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        />
        {items}
        <Pagination.Next
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        />
      </Pagination>
    );
  };

  if (loading) {
    return <div className="text-center py-5">Loading blogs...</div>;
  }
  // console.log(blogs)
  // console.log("EditBlog:",editBlog);

  return (
    <div className="blog-list">
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {showAddBlog && (
        <BlogEditor
          show={showAddBlog}
          handleClose={() => {
            setShowAddBlog(false);
            // setEditBlog(null);
          }}
          blogToEdit={editBlog}
          blogs={blogs}
          onSave={handleSaveBlog}
          onExited={() => setEditBlog(null)}
        />
      )}

      {showPreview && previewBlog && (
        <BlogPreview
          show={showPreview}
          handleClose={() => setShowPreview(false)}
          blog={previewBlog}
        />
      )}

      {showFaqEditor && selectedBlogForFaq && (
        <FaqEditor
          show={showFaqEditor}
          handleClose={() => {
            setShowFaqEditor(false);
            setSelectedBlogForFaq(null);
          }}
          blog={selectedBlogForFaq}
        />
      )}

      {blogs.length === 0 ? (
        <div className="text-center py-5">
          <p>No blogs found. Create your first blog!</p>
        </div>
      ) : (
        <>
          <Table responsive className="table-hover">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Author</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.map((blog) => (
                <tr key={blog.slug}>
                  <td>{blog.title}</td>
                  <td>
                    <Badge
                      bg="info"
                      className="category-badge"
                      onClick={() => handleCategoryFilter(blog.category_name)}
                      style={{ cursor: 'pointer' }}
                    >
                      {blog.category.name}
                    </Badge>
                  </td>
                  <td>{blog.created_by}</td>
                  <td>
                    {blog.updatedAt
                      ? new Date(blog.updatedAt).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handlePreview(blog)}
                        title="Preview"
                      >
                        <FontAwesomeIcon icon={faEye}  />
                      </Button>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => handleEdit(blog)}
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faPen} color="#ffc107" />
                      </Button>
                      <Button
                        variant={
                          blog.status ? 'outline-success' : 'outline-success'
                        }
                        size="sm"
                        onClick={() => handleTogglePublish(blog)}
                        title={blog.status ? 'Unpublish' : 'Publish'}
                      >
                        {blog.status ? (
                          <FontAwesomeIcon icon={faBan} color="#198754" />
                        ) : (
                            <FontAwesomeIcon icon={faCloudArrowUp} color="#198754" />
                        )}
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleManageFaqs(blog)}
                        title="Manage FAQs"
                      >
                        <FontAwesomeIcon icon={faCircleQuestion} color="#0dcaf0"/>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        // onClick={() => handleDelete(blog.slug)}
                        onClick={() => handleDelete(blog.id)}
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default BlogList;
